import { useCallback, useRef, useState } from "react";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { IconButton } from "domains/ui/components/Button";
import Icon from "domains/ui/components/Icon";
import { useHandleApiError } from "infra/api/error";
import {
  GetCollectionsByCollectionIdApiResponse,
  usePutCollectionsByCollectionIdMutation,
} from "infra/api/generated/api";

import { HStack, Input, Skeleton, Text } from "@chakra-ui/react";

interface FragmentCollectionTitleProps {
  collection: GetCollectionsByCollectionIdApiResponse["collection"] | undefined;
}

export default function FragmentCollectionTitle({
  collection,
}: FragmentCollectionTitleProps) {
  const { successToast } = useScenarioToast();
  const handleApiError = useHandleApiError();
  const { selectedProject } = useTeamContext();
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string | undefined>(undefined);
  const [updateCollectionTrigger, { isLoading: isUpdating }] =
    usePutCollectionsByCollectionIdMutation();

  const isSkeleton = !collection;
  const collectionId = collection?.id;
  const collectionName = collection?.name;

  // ----------------------------------

  const updateCollectionName = useCallback(async () => {
    if (!collectionId || !inputValue) {
      return;
    }

    try {
      await updateCollectionTrigger({
        collectionId,
        projectId: selectedProject.id,
        body: {
          name: inputValue,
        },
      });

      successToast({
        title: "Collection's name updated",
      });

      setTimeout(() => setIsEditing(false), 500);
    } catch (error) {
      handleApiError(
        error,
        "There was an error updating the collection's name"
      );
    }
  }, [
    collectionId,
    successToast,
    selectedProject.id,
    updateCollectionTrigger,
    handleApiError,
    inputValue,
  ]);

  // ----------------------------------

  const handleEditClick = useCallback(() => {
    setInputValue(collection?.name ?? "");
    setIsEditing(true);
  }, [collection?.name]);

  // ----------------------------------

  if (isEditing) {
    return (
      <HStack spacing={3}>
        <Input
          ref={inputRef}
          fontSize="md"
          fontWeight="600"
          autoFocus
          isRequired
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              void updateCollectionName();
            }
          }}
          size="sm"
          value={inputValue}
        />

        <IconButton
          borderRadius="full"
          aria-label="save"
          icon={<Icon id="Ui/Check" h="10px" />}
          isLoading={isUpdating}
          onClick={updateCollectionName}
          size="xs"
          variant="secondary"
        />
      </HStack>
    );
  }

  return (
    <HStack overflow="hidden" maxW="60%" spacing={3}>
      <Text lineHeight={1.2} wordBreak="break-word" size="title.xl">
        {isSkeleton ? <Skeleton w="300px" h="50px" /> : collectionName}
      </Text>

      <IconButton
        borderRadius="full"
        aria-label="edit"
        icon={<Icon id="Ui/Pencil" h="12px" />}
        onClick={handleEditClick}
        size="xs"
        variant="secondary"
      />
    </HStack>
  );
}
